import { sdk } from '@/services/sdk';
import { rows } from '@/shared/data';

export const AUDIENCE_LABELS = {
  All: 'Everyone who ever ordered',
  Active: 'Everyone with a live line',
  Expiring: 'Lines ending in the next 7 days',
  Lapsed: 'Paid before, nothing live now',
  'Opted in': 'Opted in to offers',
};

export const KIND_HINTS = {
  Service: 'Outages, maintenance, server changes. Goes to everyone in the audience. Not for sales.',
  Promo: 'Discounts and offers. Only reaches people who ticked "send me offers" and have not unsubscribed.',
};

const LIVE = ['Active', 'Trial'];
const WEEK = 7 * 24 * 60 * 60 * 1000;
const keyOf = (email) => String(email || '').trim().toLowerCase();

/**
 * Who a broadcast reaches right now: [{ name, email }], sorted by name.
 * Service notices go to the whole audience; promos only to people who opted in.
 */
export async function audienceOf(audience, kind) {
  const [customers, orders] = await Promise.all([
    sdk
      .table('customers_c')
      .select(['Name', 'email_c', 'marketing_opt_in_c', 'unsubscribed_at_c'])
      .limit(1000)
      .fetch()
      .then(rows),
    sdk
      .table('orders_c')
      .select(['full_name_c', 'email_c', 'status_c', 'activated_at_c', 'ends_at_c'])
      .limit(2000)
      .fetch()
      .then(rows),
  ]);

  const now = Date.now();
  const people = new Map();
  const blank = (email, name) => ({ email, name, optIn: false, ordered: false, live: false, expiring: false, paid: false });

  for (const c of customers) {
    const email = keyOf(c.email_c);
    if (!email) continue;
    const person = blank(email, c.Name || email);
    person.optIn = !!c.marketing_opt_in_c && !c.unsubscribed_at_c;
    people.set(email, person);
  }

  for (const o of orders) {
    const email = keyOf(o.email_c);
    if (!email) continue;
    const person = people.get(email) ?? blank(email, o.full_name_c || email);
    const ends = o.ends_at_c ? new Date(o.ends_at_c).getTime() : null;
    const live = LIVE.includes(o.status_c) && (ends === null || ends > now);
    person.ordered = true;
    if (live) person.live = true;
    if (live && ends !== null && ends - now <= WEEK) person.expiring = true;
    if (o.activated_at_c || o.status_c === 'Active') person.paid = true;
    people.set(email, person);
  }

  const rules = {
    All: (p) => p.ordered,
    Active: (p) => p.live,
    Expiring: (p) => p.expiring,
    Lapsed: (p) => p.paid && !p.live,
    'Opted in': (p) => p.optIn,
  };

  let list = [...people.values()].filter(rules[audience] ?? rules.All);
  if (kind === 'Promo') list = list.filter((p) => p.optIn);
  return list.map(({ name, email }) => ({ name, email })).sort((a, b) => a.name.localeCompare(b.name));
}
